/** Stores Issue: Internal on e-Indent / Physical Indent, Stock Transfer, Material Return to Vendor. */
import { STORES_DOC_TABS } from "@nexseed/shared";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { PackageMinus } from "lucide-react";
import { IssueListPage } from "@/features/stores/components/issue-list-page";
import { ChooserGate } from "@/features/stores/components/type-chooser";
import { issueTypeOptions } from "@/features/stores/type-options";

const GROUPS = [
  "eindent",
  "physical",
  "stock_transfer",
  "return_vendor",
] as const;

export const Route = createFileRoute("/_app/stores/issues/")({
  validateSearch: z.object({
    // No default: the Stores menu lands here with no type and the pop-up asks (user, 2026-10-08).
    group: z.enum(GROUPS).optional(),
    tab: z.enum([...STORES_DOC_TABS, "waiting"]).default("submitted"),
    from: z.string().optional(),
    to: z.string().optional(),
    q: z.string().optional(),
    page: z.number().int().min(1).default(1),
    view: z.enum(["transit"]).optional(),
  }),
  beforeLoad: () => {
    if (!useAuth.getState().can("stores.view")) throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  if (!ready) return <ListPageSkeleton />;
  const { group } = s;
  if (!group)
    return (
      <ChooserGate
        pageTitle="Issue"
        breadcrumbs={[{ label: "Stores" }, { label: "Issue" }]}
        chooserTitle="Transaction: Issue Types"
        chooserDescription="Where are the items going?"
        options={issueTypeOptions(GROUPS)}
        icon={PackageMinus}
        onPick={(g) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              group: g as (typeof GROUPS)[number],
              page: 1,
            }),
          })
        }
      />
    );
  return (
    <IssueListPage
      title="Issue"
      description="Stores items going out: to the plant floor against an indent, to another of our plants, or back to the vendor."
      groups={GROUPS}
      facilityId={selected?.id}
      search={{ ...s, group }}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) as never })
      }
      onChangeType={() =>
        void navigate({
          search: (prev) => ({ ...prev, group: undefined, view: undefined }),
        })
      }
    />
  );
}
